import {FC, ReactNode, useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {icon_triangle_fill} from '@shared_web/components/icons/icon_triangle_fill';

import {useAppTheme} from '@mavismart_frontend/theme';

interface CollapsableSectionProps {
  header?: ReactNode;
  children?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export const CollapsableSection: FC<CollapsableSectionProps> = props => {
  const {header, children, isOpen, onOpenChange} = props;
  const theme = useAppTheme();

  const [isOpenInternal, setIsOpenInternal] = useState(isOpen ?? false);

  // Reset the open state when the prop changes
  useEffect(() => setIsOpenInternal(isOpen ?? false), [isOpen]);

  // Call appropriate callback when the open state changes
  useEffect(() => onOpenChange?.(isOpenInternal), [isOpenInternal, onOpenChange]);

  const handleClick = useCallback(() => {
    setIsOpenInternal(curr => !curr);
  }, []);

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <Wrapper $column $isOpen={isOpenInternal} onClick={handleClick}>
      <Header $isOpen={isOpenInternal}>
        <SvgIcon
          icon={icon_triangle_fill}
          rotate={isOpenInternal ? 180 : 90}
          size={7}
          color={`${theme.main?.textColor}88`}
        />
        {header}
      </Header>
      {isOpenInternal && <Content onClick={handleContentClick}>{children}</Content>}
    </Wrapper>
  );
};

CollapsableSection.displayName = 'CollapsableSection';

const HPadding = '14px';
const VPadding = '8px';

const Wrapper = styled(Flex)<{$isOpen: boolean}>`
  border-radius: 6px;
  border: solid 1px #00000016;
`;

const Header = styled.div<{$isOpen: boolean}>`
  width: calc(100% + ${HPadding} * 2);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${VPadding} ${HPadding};
  background-color: #00000008;
  user-select: none;
  cursor: pointer;
  ${p => `border-bottom: solid 1px ${p.$isOpen ? '#00000016' : 'transparent'};`}
`;

const Content = styled.div`
  padding: ${VPadding} ${HPadding};
`;
